<template>
	<div v-if="products?.length" class="flex flex-wrap relative flex-1 lg:ml-8">
		<div v-if="rowView" class="mb-4 flex justify-end w-full">
			<button
				class="lg:max-w-[316px] w-full h-full bg-main-button py-3 text-white font-medium text-base px-3 hover:bg-gray-900 transition ease-in-out duration-200 disabled:text-gray-500 disabled:bg-gray-100"
				@click="handleAddAllToCart"
			>
				{{ $t("Add to Cart") }}
			</button>
		</div>
		<div
			class="grid gap-8 w-full"
			:class="rowView ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'"
		>
			<div v-for="(product, index) in products" :key="product.Id">
				<template v-if="index < 2">
					<ProductCard
						:product="product"
						:picture-size="`w-full h-auto `"
						:picture-aspect-ratio="pictureAspectRatio"
						:class="rowView ? 'min-h-[82px]' : 'min-h-[300px]'"
						:lazy-loading="false"
						:row-view="rowView"
						:quantity="rowView ? productsRowQuantity[product.Id] || 0 : 1"
						@update:quantity="updateQuantity(product.Id, $event)"
					/>
				</template>
				<template v-else>
					<LazyLoadWrapper>
						<ProductCard
							:product="product"
							:picture-size="`w-full h-auto `"
							:picture-aspect-ratio="pictureAspectRatio"
							:class="rowView ? 'min-h-[82px]' : 'min-h-[300px]'"
							:lazy-loading="false"
							:row-view="rowView"
							:quantity="rowView ? productsRowQuantity[product.Id] || 0 : 1"
							@update:quantity="updateQuantity(product.Id, $event)"
						/>
					</LazyLoadWrapper>
				</template>
			</div>
		</div>
		<div
			v-if="!isLoaded"
			class="my-8 grid gap-8 w-full"
			:class="rowView ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'"
		>
			<div
				v-for="i in scheletonItems()"
				:key="i"
				class="bg-gray-light border-gray-lighter border animate-pulse w-full"
				:class="rowView ? 'h-[82px]' : 'h-[400px]'"
			/>
		</div>
		<div v-if="rowView" class="mt-4 flex justify-end w-full">
			<button
				class="lg:max-w-[316px] w-full h-full bg-main-button py-3 text-white font-medium text-base px-3 hover:bg-gray-900 transition ease-in-out duration-200 disabled:text-gray-500 disabled:bg-gray-100"
				@click="handleAddAllToCart"
			>
				{{ $t("Add to Cart") }}
			</button>
		</div>
		<LazyLoadWrapper>
			<div
				v-if="showLoadMoreButton"
				class="flex flex-col justify-center items-center w-full mt-8"
			>
				<span class="text-secondary mb-4">
					<strong>{{ products.length }}</strong> {{ $t("of") }}
					<strong>{{ filteredProducts?.ItemCount }}</strong>
					{{ $t("products") }}
				</span>
				<Button
					:text="$t('load_more')"
					button-style="button-main-button"
					button-height="h-12"
					@click="emit('getNextPageOfProducts')"
				/>
			</div>
		</LazyLoadWrapper>
	</div>
	<div
		v-else
		class="flex flex-wrap relative flex-1 items-center justify-center min-h-[400px]"
	>
		<span class="bg-gray-lighter p-4"> {{ $t("no_more_products") }}</span>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, onMounted } from "vue";
import type {
	FilteredProductItem,
	FilteredProducts
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import ProductCard from "../Product/ProductCard/ProductCard.vue";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import { useGtmEvents } from "~/composables/useGtmEvents";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

interface Props {
	filteredProducts: FilteredProducts;
	pageSize: string;
	pictureAspectRatio: string | undefined;
	pageNo: number | undefined;
	isLoaded: boolean | undefined;
	categoryName?: string;
	rowView?: boolean;
}

const {
	filteredProducts,
	pageSize,
	pictureAspectRatio,
	pageNo,
	isLoaded,
	categoryName,
	rowView
} = defineProps<Props>();

const emit = defineEmits(["getNextPageOfProducts"]);

const { productImpression } = useGtmEvents();
const cartStore = useCartStore();

const products = ref<Partial<FilteredProductItem>[]>([]);
const filteredProductsRef = toRef(() => filteredProducts);
const LIMIT = +pageSize;
const pageNoRef = toRef(() => pageNo);
const productsRowQuantity = ref({});

const updateProducts = ({
	filteredProductsRef
}: {
	filteredProductsRef: Partial<FilteredProductItem>[];
}) => {
	products.value =
		pageNoRef.value === 1
			? [...filteredProductsRef]
			: [...products.value, ...filteredProductsRef];
};

const showLoadMoreButton = computed(() => {
	return (
		filteredProductsRef.value?.ItemCount > LIMIT &&
		products.value.length !== filteredProductsRef.value?.ItemCount
	);
});

const handleAddAllToCart = () => {
	const itemsToAdd = products.value
		.map((product) => ({
			Id: product.Id,
			PartNo: product.PartNo,
			PriceListId: product.PriceListId,
			Quantity: productsRowQuantity.value[product.Id] ?? 0
		}))
		.filter(
			(product) => product.PartNo && product.Quantity > 0
		) as BasketItem[];

	if (itemsToAdd.length > 0) {
		cartStore.addItemsToCart(itemsToAdd);
	}
};
const updateQuantity = (productId: number | undefined, qty: number) => {
	if (!productId) {
		return;
	}
	productsRowQuantity.value[productId] = qty;
};

updateProducts({ filteredProductsRef: filteredProductsRef?.value?.Items });

onMounted(() => {
	productImpression(products.value, categoryName);
});

watch(filteredProductsRef, () => {
	updateProducts({ filteredProductsRef: filteredProductsRef?.value?.Items });
	productImpression(products.value, categoryName);
});

const scheletonItems = () => {
	if (pageNoRef.value === 1) {
		return LIMIT;
	}
	if (filteredProductsRef?.value?.ItemCount + LIMIT < LIMIT) {
		return LIMIT;
	}

	return filteredProductsRef?.value?.ItemCount - products.value.length;
};
</script>
